import React, { useState, useEffect } from 'react';
import ApiRequestService from '../../../../../components/ApiRequestService';

const SFAssessmentObjectivesGrid = ({ selectedSubcategoryKeys = [], onAnswerChoice, selectedAnswers }) => {
    const [groups, setGroups] = useState([]);
    const [checkStates, setCheckStates] = useState({});
    const [expanded, setExpanded] = useState({});

    const keysStr = [...selectedSubcategoryKeys].sort().join(',');

    // Initialize checkStates from selectedAnswers (JSON with covered/want_to_assess)
    useEffect(() => {
        if (!selectedAnswers) return;
        try {
            const saved = typeof selectedAnswers === 'string' ? JSON.parse(selectedAnswers) : selectedAnswers;
            const coveredSet = new Set((saved.covered || '').split(',').filter(Boolean));
            const wantSet = new Set((saved.want_to_assess || '').split(',').filter(Boolean));
            setCheckStates(prev => {
                const next = { ...prev };
                selectedSubcategoryKeys.forEach(key => {
                    if (!next[key]) {
                        next[key] = {
                            covered: coveredSet.has(key) || wantSet.has(key),
                            wantToAccess: wantSet.has(key),
                        };
                    }
                });
                return next;
            });
        } catch (e) { /* ignore malformed data */ }
    }, []);

    useEffect(() => {
        if (!keysStr) { setGroups([]); return; }

        const parsed = keysStr.split(',').map(key => {
            const [subject_id, topic_id, category_id, subcategory_id] = key.split('_').map(Number);
            return { key, subject_id, topic_id, category_id, subcategory_id };
        });

        const byCategory = {};
        parsed.forEach(p => {
            const ck = `${p.subject_id}_${p.topic_id}_${p.category_id}`;
            if (!byCategory[ck]) byCategory[ck] = { ...p, items: [] };
            byCategory[ck].items.push(p);
        });

        Promise.all(
            Object.values(byCategory).map(async (group) => {
                const [catRes, subRes] = await Promise.all([
                    ApiRequestService({
                        method: 'post', url: '/getAllCategories',
                        data: { subject_id: group.subject_id, topic_id: group.topic_id },
                    }),
                    ApiRequestService({
                        method: 'post', url: '/getAllSubCategories',
                        data: { subject_id: group.subject_id, topic_id: group.topic_id, category_id: group.category_id },
                    }),
                ]);

                const categories = catRes?.data?.data?.category_names || [];
                const subcategories = subRes?.data?.data?.subCategory_names || [];
                const selectedIds = new Set(group.items.map(i => String(i.subcategory_id)));
                const cat = categories.find(c => String(c.category_id) === String(group.category_id));

                return {
                    category_name: cat?.category_name || '',
                    subcategories: subcategories
                        .filter(sc => selectedIds.has(String(sc.subcategory_id)))
                        .map(sc => ({
                            key: group.items.find(i => String(i.subcategory_id) === String(sc.subcategory_id)).key,
                            subcategory_name: sc.subcategory_name,
                        })),
                };
            })
        ).then(setGroups);
    }, [keysStr]);

    const getState = (key) => checkStates[key] || { covered: false, wantToAccess: false };

    const emitAnswer = (nextStates) => {
        const coveredKeys = Object.entries(nextStates)
            .filter(([, v]) => v.covered)
            .map(([k]) => k);
        const wantToAccessKeys = Object.entries(nextStates)
            .filter(([, v]) => v.wantToAccess)
            .map(([k]) => k);
        onAnswerChoice({
            covered: coveredKeys.join(','),
            want_to_assess: wantToAccessKeys.join(','),
        });
    };

    const toggleCovered = (key) => {
        setCheckStates(prev => {
            const cur = prev[key] || { covered: false, wantToAccess: false };
            const nextCovered = !cur.covered;
            const next = {
                ...prev,
                [key]: {
                    covered: nextCovered,
                    wantToAccess: nextCovered ? cur.wantToAccess : false,
                },
            };
            emitAnswer(next);
            return next;
        });
    };

    const toggleWantToAccess = (key) => {
        setCheckStates(prev => {
            const cur = prev[key] || { covered: false, wantToAccess: false };
            const nextWant = !cur.wantToAccess;
            const next = {
                ...prev,
                [key]: {
                    covered: nextWant ? true : cur.covered,
                    wantToAccess: nextWant,
                },
            };
            emitAnswer(next);
            return next;
        });
    };

    const toggleAllCovered = (group) => {
        setCheckStates(prev => {
            const next = { ...prev };
            const allCovered = group.subcategories.every(sc => (next[sc.key] || { covered: false }).covered);
            group.subcategories.forEach(sc => {
                const cur = next[sc.key] || { covered: false, wantToAccess: false };
                next[sc.key] = allCovered
                    ? { covered: false, wantToAccess: false }
                    : { ...cur, covered: true };
            });
            emitAnswer(next);
            return next;
        });
    };

    const toggleAllWantToAccess = (group) => {
        setCheckStates(prev => {
            const next = { ...prev };
            const allWant = group.subcategories.every(sc => (next[sc.key] || { wantToAccess: false }).wantToAccess);
            group.subcategories.forEach(sc => {
                const cur = next[sc.key] || { covered: false, wantToAccess: false };
                next[sc.key] = allWant
                    ? { ...cur, wantToAccess: false }
                    : { covered: true, wantToAccess: true };
            });
            emitAnswer(next);
            return next;
        });
    };

    if (groups.length === 0) return null;

    return (
        <div style={{ marginTop: 8 }}>
            {groups.map(group => {
                const isOpen = !!expanded[group.category_name];
                return (
                    <div key={group.category_name} style={{ marginBottom: 8 }} className='sf-ao-table'>
                        <div
                            style={{
                                fontWeight: 600, fontSize: 14, padding: '8px 8px',
                                cursor: 'pointer', userSelect: 'none',
                                background: '#fafafa', borderRadius: '4px', color: '#747474',
                                marginBottom:"6px"
                            }}
                            onClick={() => setExpanded(prev => ({ ...prev, [group.category_name]: !prev[group.category_name] }))}
                        >
                            <span style={{ display: 'inline-block', width: 16, marginRight: 4 }}>{isOpen ? '▾' : '▸'}</span>
                            {group.category_name}
                        </div>
                        {isOpen && (
                            <table className="w-100" style={{ marginBottom: 0, borderCollapse: 'separate', borderSpacing: '0px 10px'  }}>
                                <thead style={{ background: '#e8e8e8', fontSize: '14px', color: '#747474' }}>
                                    <tr>
                                        <th style={{ textAlign: 'left', }}>
                                            <div className='px-3'>
                                                Assessment Objective
                                            </div>
                                        </th>
                                        <th style={{ textAlign: 'center', width: 100, height: 62 }}>
                                            <div style={{
                                                display:"flex",
                                                flexDirection:"column",
                                                alignItems:"center",
                                                justifyContent:"flex-end",
                                                height:"100%"
                                            }}>
                                            Covered
                                                <div>
                                                    <input
                                                        style={{ width: 18, height: 18 }}
                                                        type="checkbox"
                                                        checked={group.subcategories.every(sc => getState(sc.key).covered)}
                                                        onChange={() => toggleAllCovered(group)}
                                                    />
                                                </div>
                                            </div>
                                        </th>
                                        <th style={{ textAlign: 'center', width: 100 }}>
                                            Want to Assess
                                            <div>
                                                <input
                                                    style={{ width: 18, height: 18 }}
                                                    type="checkbox"
                                                    checked={group.subcategories.every(sc => getState(sc.key).wantToAccess)}
                                                    onChange={() => toggleAllWantToAccess(group)}
                                                />
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.subcategories.map(sc => {
                                        const st = getState(sc.key);
                                        return (
                                            <tr key={sc.key} >
                                                <td>
                                                    <div className='px-4'>
                                                        {sc.subcategory_name}
                                                    </div>
                                                </td> 
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        style={{ width: 18, height: 18 }}
                                                        type="checkbox"
                                                        checked={st.covered}
                                                        onChange={() => toggleCovered(sc.key)}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        style={{ width: 18, height: 18 }}
                                                        type="checkbox"
                                                        checked={st.wantToAccess}
                                                        onChange={() => toggleWantToAccess(sc.key)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SFAssessmentObjectivesGrid;
